import { useCallback, useEffect, useRef, useState } from "react";
import { useAPI } from "../providers/ApiProvider";

// Small data-fetching hook for dashboard endpoints. Purpose is to keep the
// chart page components lean: one line to pull a time-series, with
// cancellation and a monotonic request-id guard so stale responses from a
// user switching ranges quickly don't clobber fresh data.
export const useMetrics = (apiMethod, params, deps = []) => {
  const api = useAPI();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const reqIdRef = useRef(0);

  // Abort the previous in-flight request when a new one starts (range
  // switching). Before, rapid 7D→30D→90D taps left every request in
  // flight; on a slow link they queued behind each other, starved the
  // reachability probe, and the app flipped to "offline". Also saves
  // battery/data on real phones.
  const abortRef = useRef(null);
  const refetch = useCallback(() => {
    if (!apiMethod) return;
    const reqId = ++reqIdRef.current;
    try {
      abortRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    const ac = typeof AbortController !== "undefined" ? new AbortController() : null;
    abortRef.current = ac;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve(api.callApi(apiMethod, { ...(params ?? {}), ...(ac ? { signal: ac.signal } : {}) }))
      .then((res) => {
        if (reqId !== reqIdRef.current) return; // stale
        if (!res || res.error) {
          setState({ loading: false, data: null, error: res?.error ?? "Failed to load" });
        } else {
          setState({ loading: false, data: res, error: null });
        }
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        if (err?.name === "AbortError") return; // superseded — not an error
        setState({ loading: false, data: null, error: err?.message ?? String(err) });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, apiMethod, JSON.stringify(params)]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, ...deps]);

  return { ...state, refetch };
};
